import { Box } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AndroidAppBannerView } from '#features/app-install/components/AndroidAppBanner';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { Panel } from '#features/ui';

/** Actual banner UI; dismissing this fixture never changes account storage or analytics. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/android-app-banner')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    placement:
      search.placement === 'chats-banner'
        ? ('chats-banner' as const)
        : search.placement === 'discover-banner'
          ? ('discover-banner' as const)
          : ('feed-banner' as const),
    narrow: search.narrow === true || search.narrow === 'true',
  }),
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: BannerPreview,
});

const noop = () => {};

function BannerPreview() {
  const { lang, placement, narrow } = Route.useSearch();
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  return (
    <I18nProvider i18n={i18n}>
      <BannerExample key={`${lang}-${placement}-${narrow}`} placement={placement} narrow={narrow} />
    </I18nProvider>
  );
}

function BannerExample({
  placement,
  narrow,
}: {
  placement: 'feed-banner' | 'discover-banner' | 'chats-banner';
  narrow: boolean;
}) {
  const [dismissed, setDismissed] = useState(false);
  const banner = dismissed ? null : (
    <AndroidAppBannerView
      placement={placement}
      onClose={() => setDismissed(true)}
      onInstallClick={noop}
    />
  );
  return (
    <Box w="100%" maxW={narrow ? '320px' : undefined} mx="auto" minW={0}>
      {placement === 'chats-banner' ? (
        <Panel variant="object">
          {banner}
          <Box h={48} />
        </Panel>
      ) : (
        <>
          {banner}
          <Panel variant="container" minH={48} />
        </>
      )}
    </Box>
  );
}
